import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { DescriptorsComponent } from '../../../../components/descriptor/descriptor.component';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import { REPORT_NAMES } from '../../../../components/reports/constants/report-names.constants';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { FilterSelectedValue } from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { MassEditBlock } from '../mass-edit-sidebar/mass-edit-sidebar.component';
import ScenarioPlannerEditInputFieldComponent from './scenario-planner-edit-input-field.component';
interface CrossElastRow {
  id: string;
  compId: string;
  name?: string;
  category_name?: string;
  [key: string]: any;
}
interface InputField {
  value: any;
  fieldId: string;
  id: string;
  compId: string;
  isOverwritten: boolean;
}
export interface MassEditBlockCross extends MassEditBlock {
  id: string;
  compId: string;
}
interface ScenarioPlannerEditCrossElastComponentProps {
  onClose: (show: boolean) => void;
  scenarioPlannerId: string;
  onComplete: (
    inputFields: MassEditBlockCross[],
    invalidateQuery: () => Promise<void>,
  ) => void;
  onBack: () => void;
  disabled?: boolean;
  resetButton: ReactNode;
  applyToFilterSelection: FilterSelectedValue[];
  isSaving?: boolean;
}
const getKey = (fieldId: string, id: string, compId: string) => {
  return `${fieldId}-${id}-${compId}`;
};
export const ScenarioPlannerEditCrossElastComponent = ({
  isSaving,
  onClose,
  scenarioPlannerId,
  onComplete,
  onBack,
  disabled,
  resetButton,
  applyToFilterSelection,
}: ScenarioPlannerEditCrossElastComponentProps) => {
  // Dynamic form state based on API response measure columns. This is updated by the user inputs.
  const [crossElast, setCrossElast] = useState<{
    [key: string]: InputField;
  }>();
  // This stores initial main elasticities and once it is loaded doesn't change.
  const [initCrossElast, setInitCrossElast] = useState<{
    [key: string]: InputField;
  }>();

  const sortBy = useMemo(() => [{ id: 'name', desc: false }], []);

  const { data, isFetching, invalidateQuery } = useDataProvider<CrossElastRow>(
    REPORT_NAMES.SCENARIO_PLANNER_CROSS_ELASTICITY,
    applyToFilterSelection,
    sortBy,
    !disabled,
    true,
    scenarioPlannerId || '',
  );

  const columns = useMemo(() => {
    return data?.columns ?? [];
  }, [data]);

  const measureColumns = useMemo(() => {
    return data?.measureColumns ?? [];
  }, [data]);

  // Initialize starting metrics form data when API data loads
  useEffect(() => {
    if (isFetching) {
      // Clear values to be prepared for upcoming result.
      setCrossElast(undefined);
      setInitCrossElast(undefined);
      return;
    }
    const measureColumns = data?.measureColumns;
    if (!measureColumns || measureColumns.length === 0) return;
    const values: Record<string, InputField> = {};
    measureColumns.forEach((i) => {
      if (data?.rows) {
        data.rows.forEach((row) => {
          const key = getKey(i.id, row.id, row.comp_id);
          const isOverwritten: boolean = row[`${i.id}IsOverwritten`] ?? false;
          if (isOverwritten) {
            values[key] = {
              value: row[i.id],
              fieldId: i.id,
              compId: row.comp_id,
              id: row.id,
              isOverwritten: row[`${i.id}IsOverwritten`],
            };
          }
        });
      }
    });
    // Values to be modified by the user.
    setCrossElast(values);
    // Initial values to display saved one.
    setInitCrossElast(values);
  }, [data, isFetching]);

  const handleChange = useCallback(
    (key: string, value: any, fieldId: string, id: string, compId: string) => {
      setCrossElast((prev) => {
        if (prev) {
          return {
            ...prev,
            [key]: {
              ...prev[key],
              fieldId,
              id,
              compId,
              value,
            },
          };
        } else {
          return {
            [key]: {
              fieldId,
              id,
              compId,
              value,
            },
          } as { [key: string]: InputField };
        }
      });
    },
    [],
  );

  const handleNext = useCallback(() => {
    if (!crossElast) {
      return;
    }
    const inputFields: MassEditBlockCross[] = [];
    // Dynamically create input fields based on elasticity values
    Object.entries(crossElast).forEach(([key, field]) => {
      if (field.value && field.value !== '') {
        // New values
        inputFields.push({
          fieldId: `cross_${field.fieldId}`, // Prefix with 'cross_' for starting metrics
          inputType: 'abs',
          value: parseFloat(field.value),
          id: field.id,
          compId: field.compId,
        });
      }
      if (field.isOverwritten && field.value === '') {
        // Erased values
        inputFields.push({
          fieldId: `cross_${field.fieldId}`, // Prefix with 'cross_' for starting metrics
          inputType: 'abs',
          value: undefined,
          compId: field.compId,
          id: field.id,
        });
      }
    });
    onComplete(inputFields, invalidateQuery);
  }, [crossElast, onComplete, invalidateQuery]);

  const internalDisabled = useMemo(() => {
    return isFetching || disabled || isSaving;
  }, [isFetching, disabled, isSaving]);
  return (
    <div className="p-4">
      <LoadingComponent
        isLoading={isFetching}
        message="Loading elasticity data..."
      >
        <div className="container-fluid">
          <div className="row mb-4">
            <div className="col-12">
              <div className="table-container">
                <table className="table table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.id}
                          className={`${col.descriptors ? 'descriptor' : ''}`}
                        >
                          {col.name}
                        </th>
                      ))}
                      {measureColumns.map((col) => (
                        <th key={col.id}>{col.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {initCrossElast &&
                      data?.rows.map((row, index) => (
                        <tr key={index}>
                          {columns.map((col) => (
                            <td
                              key={col.id}
                              className={`${col.descriptors ? 'descriptor' : ''}`}
                            >
                              {col.descriptors && (
                                <div className="prod-cell-text">
                                  <div>
                                    <div>
                                      {row?.metadata
                                        ? row?.metadata[col.id].description
                                        : ''}
                                    </div>
                                    <DescriptorsComponent
                                      metadata={row?.metadata[col.id]}
                                    />
                                  </div>
                                </div>
                              )}
                              {col.descriptors === false && row && row[col.id]}
                            </td>
                          ))}
                          {measureColumns.map((col, index) => {
                            const key = getKey(col.id, row.id, row.comp_id);
                            const inputField = initCrossElast[key];
                            return (
                              <td key={index}>
                                {row && (
                                  <ScenarioPlannerEditInputFieldComponent
                                    value={inputField?.value}
                                    isOverwritten={
                                      row[`${col.id}IsOverwritten`]
                                    }
                                    placeholder={row[`${col.id}Original`]}
                                    onChange={(fId, value) =>
                                      handleChange(
                                        key,
                                        value,
                                        col.id,
                                        row.id,
                                        row.comp_id,
                                      )
                                    }
                                    fieldId={col.id}
                                    disabled={internalDisabled}
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 d-flex justify-content-end gap-3">
              {resetButton}
              <Button
                variant="outline-secondary"
                onClick={() => onBack()}
                disabled={internalDisabled}
              >
                Back
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => onClose(false)}
                disabled={internalDisabled}
              >
                Discard Changes
              </Button>
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={internalDisabled}
              >
                {isSaving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  'Save Elasticities'
                )}
              </Button>
            </div>
          </div>
        </div>
      </LoadingComponent>
    </div>
  );
};
